import { useState, useEffect } from "react";
import { X, ExternalLink, Trash2 } from "lucide-react";
import { ARCHETYPE_MAP, ARCHETYPE_GROUPS } from "../constants/archetypes";
import { usePanelMode } from "../hooks/usePanelMode";
import { InstallCard } from "./InstallCard";
import { CreditsCard } from "./Credits";
import { MediaPickButton } from "./momentum/mediaPicker";
import { TERMS_URL, PRIVACY_URL } from "../constants/legal";

/* Settings is a canvas surface, so it follows the Light / Dark panel toggle —
   which happens to live on this screen, so the switch previews itself.

   The card shadow drops to none on the dark canvas; there, --bg-surface sits
   lighter than --bg-primary and carries the elevation on its own.            */
const card = { background: "var(--bg-surface)", borderRadius: 12, padding: "20px", boxShadow: "var(--shadow-soft)" };
const heading = { margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.5px" };
const field = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1.5px solid var(--border-strong)", fontSize: 14,
  outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  background: "var(--input-bg)", color: "var(--text-primary)",
};

// The knob has to stay legible against both track colours in both scopes, so
// each track pairs with the text token designed for it.
function Toggle({ on, onClick, label }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      aria-label={label}
      style={{
        width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
        background: on ? "var(--btn-bg)" : "var(--btn-off-bg)",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: "50%",
        background: on ? "var(--btn-text)" : "var(--btn-off-text)",
        position: "absolute", top: 3, left: on ? 21 : 3, transition: "left 0.2s",
      }} />
    </button>
  );
}

export function SettingsPanel({ userSettings, saveSettings, user, isMobile, onBack, onResetAll, canEraseEverything = false }) {
  /* The image picker ("Choose image") only where there IS a CRM — not everyone has GHL (her rule,
     2026-09-23); everyone else pastes a link. First, above every return. */
  /* ERASE EVERYTHING IS NOT A CLIENT'S BUTTON. Her call, 2026-09-22.
     It deletes every locked section as well as every conversation, Supabase is
     on the free tier so there is no backup to restore from, and a coach has no
     reason to want it — the day one presses it, it will have been a mis-click
     or a bad afternoon, and the answer will be "it is gone".
     Hidden rather than removed: she still needs it, and someone who emails
     asking to start over can be helped by hand. The typed confirmation behind
     it now protects HER, which is the right shape for a thing only she can do.
     A proper soft delete — mark the rows, purge after thirty days — is the
     version that actually makes the data safe, and is a bigger change.

     Handed in rather than worked out here: App already knows who the admin is,
     and a settings screen importing the whole Momentum hub to compare one
     string is a lot of module for a boolean. */
  // Same source as the header toggle, so the two can never drift apart.
  const { panelMode, setPanelMode } = usePanelMode();
  const [displayName, setDisplayName] = useState(userSettings.displayName || "");
  /* WHAT HER BUSINESS IS CALLED, AND WHAT IT LOOKS LIKE. Her ask, 2026-09-22,
     and it came out of a gap the site brief found: a site builder REQUIRES a
     business name and a logo, and Brill held neither — so the brief had to
     report them as missing on every page she copied.
     Typed once here rather than asked for per page: the name of her business
     is not a fact about a page. */
  const [photoUrl, setPhotoUrl] = useState(userSettings.photoUrl || "");
  const [savedIndicator, setSavedIndicator] = useState(false);

  useEffect(() => {
    setDisplayName(userSettings.displayName || "");
  }, [userSettings.displayName]);
  useEffect(() => { setPhotoUrl(userSettings.photoUrl || ""); }, [userSettings.photoUrl]);

  const showSaved = () => {
    setSavedIndicator(true);
    setTimeout(() => setSavedIndicator(false), 2000);
  };

  const handleDisplayNameBlur = () => {
    if (displayName !== (userSettings.displayName || "")) {
      saveSettings({ displayName });
      showSaved();
    }
  };

  /* Saved on the way out of the field, like everything else in Momentum, and
     only when it actually changed. */
  const saveOnBlur = (key, value) => () => {
    if (value !== (userSettings[key] || "")) {
      saveSettings({ [key]: value });
      showSaved();
    }
  };

  const handleSettingChange = (updates) => {
    saveSettings(updates);
    showSaved();
  };

  return (
    <div style={{
      flex: 1, overflowY: "auto", background: "var(--bg-primary)",
      padding: isMobile ? "20px 16px 100px" : "32px 32px 40px",
      display: "flex", flexDirection: "column", gap: 24,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>Settings</h2>
          <p style={{ margin: 0, fontSize: 13, color: savedIndicator ? "var(--accent)" : "var(--text-secondary)", transition: "color 0.3s" }}>
            {savedIndicator ? "✓ Saved" : "Personalize your Brill experience"}
          </p>
        </div>
        <button onClick={() => onBack()} style={{
          background: "var(--btn-bg)", border: "none", borderRadius: 8,
          padding: "9px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
          fontSize: 13, color: "var(--btn-text)", fontWeight: 600,
        }}>
          <X size={13} />
          Close
        </button>
      </div>

      {/* Display Name */}
      <div style={card}>
        <h3 style={heading}>Display Name</h3>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-secondary)" }}>What Brill calls you in conversation</p>
        <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onBlur={handleDisplayNameBlur}
            placeholder={user?.firstName || "Your name"}
            style={field}
          />
      </div>

      {/* Your business — the things a website, a deck or a CRM asks for and
          Brill had nowhere to keep. */}
      <div style={card}>
        <h3 style={heading}>Your business</h3>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-secondary)" }}>
          Used wherever Brill hands your work to something else — a site builder, a deck, your CRM.
        </p>

        {/* NAME AND LOGO MOVED TO ME → BRAND (2026-10-12), beside her
            colours. WHAT KIND OF BUSINESS moved to ME → BUSINESS (2026-10-13)
            with her website — the basics in one place. Said here, because
            this is where she last saw them. */}
        <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--text-primary)" }}>
          Your business name, website and what kind of business it is are in <b>Me → Business</b>. Your logo and colors are in <b>Me → Brand</b>.
        </p>

        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 5 }}>Photo of you</label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: photoUrl ? 8 : 0, flexWrap: "wrap" }}>
          <input type="url" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)}
            onBlur={saveOnBlur("photoUrl", photoUrl)}
            placeholder="https://… a link to your headshot"
            style={{ ...field, flex: 1, minWidth: 180, marginBottom: 0 }} />
          <MediaPickButton onPick={(url) => { setPhotoUrl(url); saveSettings({ photoUrl: url }); showSaved(); }} />
        </div>
        {photoUrl && <img src={photoUrl} alt="" style={{ maxHeight: 72, borderRadius: 8, display: "block" }} />}

        <p style={{ margin: "12px 0 0", fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.6 }}>
          Any public link works. If your photo already lives in your CRM's media library, its link is the one to paste.
        </p>
      </div>

      {/* Archetype */}
      <div style={card}>
        <h3 style={heading}>Your Archetype</h3>
        <p style={{ margin: "0 0 4px", fontSize: 12, color: "var(--text-secondary)" }}>Your Business Brilliance Archetype is your business design lens — built from your Energy Pattern (Gold, Green, Orange, Blue) and Action Instinct (Owl, Dolphin, Hawk, Beaver). It shapes how Brill coaches you, writes your copy, and builds your offer.</p>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-muted)" }}>Haven't taken the assessment yet? Set your archetype after you complete My Story, or take the assessment first.</p>
        <select
          value={userSettings.archetype || ""}
          onChange={e => {
            const name = e.target.value;
            const pair = ARCHETYPE_MAP[name] || "";
            handleSettingChange({ archetype: name, archetypePair: pair });
          }}
          style={{ ...field, color: userSettings.archetype ? "var(--text-primary)" : "var(--text-muted)" }}
        >
          <option value="">Select your archetype...</option>
          {ARCHETYPE_GROUPS.map(group => (
            <optgroup key={group.label} label={group.label}>
              {group.names.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {!userSettings.archetype && (
          <a
            href="https://tevrawood.com/assessment"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              marginTop: 10, fontSize: 12, color: "var(--accent)",
              textDecoration: "none", fontWeight: 500,
            }}
          >
            <ExternalLink size={11} />
            Don't know your archetype? Take the assessment →
          </a>
        )}
        {userSettings.archetype && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--accent-quiet)", borderRadius: 8, borderLeft: "3px solid var(--accent)" }}>
            <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>✓ {userSettings.archetype}</span>
            {userSettings.archetypePair && (
              <span style={{ fontSize: 11, color: "var(--text-secondary)", marginLeft: 6 }}>({userSettings.archetypePair})</span>
            )}
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>Brill will personalize every module around this archetype</div>
          </div>
        )}
      </div>

      {/* Notifications */}
      <div style={card}>
        <h3 style={{ ...heading, margin: "0 0 16px" }}>Notifications</h3>
        {[
          { key: "emailUpdates", label: "Program updates", desc: "Get notified about new modules and program content" },
          { key: "emailReminders", label: "Progress reminders", desc: "Weekly nudges to keep your momentum going" },
        ].map(({ key, label, desc }) => (
          <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{label}</div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{desc}</div>
            </div>
            <Toggle
              on={!!userSettings[key]}
              label={label}
              onClick={() => handleSettingChange({ [key]: !userSettings[key] })}
            />
          </div>
        ))}
      </div>

      {/* Appearance */}
      <div style={card}>
        <h3 style={{ ...heading, margin: "0 0 16px" }}>Appearance</h3>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>Dark panel</div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>Darkens the working area. Your sidebar stays the same.</div>
          </div>
          <Toggle
            on={panelMode === "dark"}
            label="Dark panel"
            onClick={() => setPanelMode(panelMode === "dark" ? "light" : "dark")}
          />
        </div>
      </div>

      {/* Install App */}
      <InstallCard />

      {/* Credits — nothing for the admin, or before supabase/0036 runs. */}
      <CreditsCard card={card} heading={heading} />

      {/* Account */}
      <div style={card}>
        <h3 style={heading}>Account</h3>
        <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--text-primary)" }}>{user?.primaryEmailAddress?.emailAddress}</p>
        <p style={{ margin: "0 0 12px", fontSize: 11, color: "var(--text-muted)" }}>Managed via Clerk — contact your program administrator to update account details</p>
        <div style={{ display: "flex", gap: 12 }}>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: 12, textDecoration: "none", fontWeight: 500 }}>Terms & Conditions</a>
          <span style={{ color: "var(--border-strong)", fontSize: 12 }}>·</span>
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: 12, textDecoration: "none", fontWeight: 500 }}>Privacy Policy</a>
        </div>
      </div>

      {/* Data — hers alone; see the note at the top of this component. */}
      {canEraseEverything && (
      <div style={card}>
        <h3 style={heading}>Data</h3>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-secondary)" }}>
          Permanently erase every conversation <b>and everything you have locked</b> — My Vision, My Client, your offer, your brand palette. This cannot be undone and there is no backup.
        </p>
        <button
          onClick={onResetAll}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "none", border: "1px solid var(--danger-line)", borderRadius: 8,
            padding: "9px 16px", cursor: "pointer", color: "var(--danger-text)", fontSize: 13, fontWeight: 500,
          }}
        >
          <Trash2 size={14} color="var(--danger-text)" />
          Erase everything
        </button>
      </div>
      )}
    </div>
  );
}
